import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    calculateCartSummary,
} from "./CartContext";

import {
    getWallet,
} from "../services/walletService";

import {
    getPaymentGateways,
} from "../services/paymentGatewayService";
import { applyCoupon as applyCouponApi } from "../services/couponService";


const PaymentContext = createContext(null);


export function PaymentProvider({
    children,
    cart = [],
    selectedShipping = null,
    
}) {


    /*
    |--------------------------------------------------------------------------
    | Payment Method
    |--------------------------------------------------------------------------
    */

    const [paymentMethod, setPaymentMethod] =
        useState("online");


    /*
    |--------------------------------------------------------------------------
    | Payment Gateways
    |--------------------------------------------------------------------------
    */

    const [gateways, setGateways] =
        useState([]);

    const [selectedGateway, setSelectedGateway] =
        useState(null);

    const [gatewayLoading, setGatewayLoading] =
        useState(true);


    /*
    |--------------------------------------------------------------------------
    | Wallet
    |--------------------------------------------------------------------------
    */

    const [wallet, setWallet] =
        useState(null);

    const [walletLoading, setWalletLoading] =
        useState(true);

    const [useWallet, setUseWallet] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | Coupon
    |--------------------------------------------------------------------------
    |
    | This keeps the exact structure returned by your DiscountCode.
    |
    */

const [couponData, setCouponData] = useState(null);
const [couponCode, setCouponCode] = useState("");
const [couponLoading, setCouponLoading] = useState(false);
const [couponStatus, setCouponStatus] = useState(null);
const [couponMessage, setCouponMessage] = useState("");

const applyCoupon = useCallback(
    async (code) => {

        const normalizedCode =
            String(code || "").trim();

        if (!normalizedCode) {

            setCouponStatus("error");

            setCouponMessage(
                "لطفاً کد تخفیف را وارد کنید."
            );

            return false;
        }

        if (couponLoading || couponData?.coupon) {
            return false;
        }

        if (!Array.isArray(cart) || cart.length === 0) {

            setCouponStatus("error");

            setCouponMessage(
                "سبد خرید شما خالی است."
            );

            return false;
        }

        if (!selectedShipping?.id) {

            setCouponStatus("error");

            setCouponMessage(
                "لطفاً ابتدا روش ارسال را انتخاب کنید."
            );

            return false;
        }

        try {

            setCouponLoading(true);

            setCouponStatus(null);

            setCouponMessage("");

            const response =
                await applyCouponApi({

                    code: normalizedCode,

                    cart,

                    shippingMethodId:
                        selectedShipping.id,

                });


            const invoice =
                response?.data;


            if (!invoice) {

                throw new Error(
                    "پاسخ نامعتبر از سرور دریافت شد."
                );

            }


            setCouponData(invoice);

            setCouponCode(
                invoice.coupon?.code ??
                normalizedCode
            );

            setCouponStatus("success");

            setCouponMessage(
                response?.message ??
                "کد تخفیف با موفقیت اعمال شد."
            );

            return true;

        } catch (error) {

            console.error(
                "Apply coupon error:",
                error
            );

            setCouponData(null);

            setCouponStatus("error");

            setCouponMessage(
                error?.response?.data?.errors?.code?.[0] ??
                error?.response?.data?.message ??
                error?.message ??
                "اعمال کد تخفیف امکان‌پذیر نیست."
            );

            return false;

        } finally {

            setCouponLoading(false);

        }

    },
    [
        couponLoading,
        couponData,
        cart,
        selectedShipping,
    ]
);


const removeCoupon = useCallback(() => {

    setCouponData(null);

    setCouponCode("");

    setCouponStatus(null);

    setCouponMessage("");

}, []);


    /*
    |--------------------------------------------------------------------------
    | Load Wallet
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        let mounted = true;

        const loadWallet = async () => {
            try {
                setWalletLoading(true);

                const data = await getWallet();

                if (mounted) {
                    setWallet(data);
                }

            } catch (error) {
                console.error(
                    "Wallet error:",
                    error
                );

                if (mounted) {
                    setWallet(null);
                }

            } finally {
                if (mounted) {
                    setWalletLoading(false);
                }
            }
        };

        loadWallet();

        return () => {
            mounted = false;
        };
    }, []);


    /*
    |--------------------------------------------------------------------------
    | Load Payment Gateways
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        let mounted = true;

        const loadGateways = async () => {
            try {
                setGatewayLoading(true);

                const data =
                    await getPaymentGateways();

                if (!mounted) {
                    return;
                }

                const availableGateways =
                    Array.isArray(data)
                        ? data.filter(
                            (gateway) =>
                                !gateway.maintenance
                        )
                        : [];

                setGateways(
                    availableGateways
                );


                /*
                |--------------------------------------------------------------------------
                | Restore previously selected gateway
                |--------------------------------------------------------------------------
                */

                let savedGateway = null;

                const savedDriver =
                    localStorage.getItem(
                        "gateway"
                    );

                if (savedDriver) {
                    try {
                        const parsed =
                            JSON.parse(savedDriver);

                        savedGateway =
                            availableGateways.find(
                                (gateway) =>
                                    gateway.driver ===
                                    parsed.driver
                            ) || null;

                    } catch {
                        savedGateway = null;
                    }
                }


                /*
                |--------------------------------------------------------------------------
                | Recommended gateway
                |--------------------------------------------------------------------------
                */

                const recommendedGateway =
                    availableGateways.find(
                        (gateway) =>
                            gateway.recommended
                    );


                /*
                |--------------------------------------------------------------------------
                | Select gateway
                |--------------------------------------------------------------------------
                */

                setSelectedGateway(
                    savedGateway ||
                    recommendedGateway ||
                    availableGateways[0] ||
                    null
                );

            } catch (error) {
                console.error(
                    "Payment gateway error:",
                    error
                );

                if (mounted) {
                    setGateways([]);
                    setSelectedGateway(null);
                }

            } finally {
                if (mounted) {
                    setGatewayLoading(false);
                }
            }
        };

        loadGateways();

        return () => {
            mounted = false;
        };
    }, []);


    /*
    |--------------------------------------------------------------------------
    | Save Selected Gateway
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        if (!selectedGateway) {
            return;
        }

        localStorage.setItem(
            "gateway",
            JSON.stringify(selectedGateway)
        );

    }, [selectedGateway]);


    /*
    |--------------------------------------------------------------------------
    | Base Cart Summary
    |--------------------------------------------------------------------------
    |
    | No coupon and no wallet here.
    |
    */

    const summary = useMemo(() => {
        return calculateCartSummary(
            cart,
            selectedShipping?.price ?? 0
        );
    }, [
        cart,
        selectedShipping?.price,
    ]);


    /*
    |--------------------------------------------------------------------------
    | Coupon Values
    |--------------------------------------------------------------------------
    */

    const couponDiscount =
        Number(
            couponData?.couponDiscount || 0
        );


    const couponPayable =
        couponData?.payable != null
            ? Number(couponData.payable)
            : Number(summary?.payable || 0);

    

    /*
    |--------------------------------------------------------------------------
    | Wallet Values
    |--------------------------------------------------------------------------
    */

    const walletBalance =
        Number(
            wallet?.balance || 0
        );


    /*
    |--------------------------------------------------------------------------
    | Wallet Used
    |--------------------------------------------------------------------------
    */

    const walletUsed =
        useWallet
            ? Math.min(
                walletBalance,
                couponPayable
            )
            : 0;


    /*
    |--------------------------------------------------------------------------
    | Final Summary
    |--------------------------------------------------------------------------
    */

    const finalSummary = useMemo(() => {

        return {

            ...summary,

            couponDiscount,

            walletUsed,

            payable:
                Math.max(
                    0,
                    couponPayable -
                    walletUsed
                ),

            appliedCoupon:
                couponData?.coupon || null,

        };

    }, [
        summary,
        couponDiscount,
        walletUsed,
        couponPayable,
        couponData,
    ]);


    /*
    |--------------------------------------------------------------------------
    | Select Gateway
    |--------------------------------------------------------------------------
    */

    const selectGateway = useCallback(
        (gateway) => {

            if (
                !gateway ||
                gateway.maintenance
            ) {
                return;
            }

            setSelectedGateway(
                gateway
            );

        },
        []
    );


    /*
    |--------------------------------------------------------------------------
    | Toggle Wallet
    |--------------------------------------------------------------------------
    */

    const toggleWallet = useCallback(
        (checked) => {

            setUseWallet(
                Boolean(checked)
            );

        },
        []
    );


    /*
    |--------------------------------------------------------------------------
    | Coupon Applied
    |--------------------------------------------------------------------------
    */

    const handleCouponApplied =
        useCallback(
            (data) => {

                setCouponData(
                    data || null
                );

            },
            []
        );


    /*
    |--------------------------------------------------------------------------
    | Remove Coupon
    |--------------------------------------------------------------------------
    */



    /*
    |--------------------------------------------------------------------------
    | Context
    |--------------------------------------------------------------------------
    */

  const value = useMemo(() => ({

    // Payment
    paymentMethod,
    setPaymentMethod,

    // Gateways
    gateways,
    selectedGateway,
    setSelectedGateway,
    selectGateway,
    gatewayLoading,

    // Wallet
    wallet,
    walletLoading,
    useWallet,
    setUseWallet,
    toggleWallet,

    // Coupon
    couponData,
    couponCode,
    setCouponCode,

    couponLoading,
    couponStatus,
    couponMessage,

    applyCoupon,
    removeCoupon,

    // Pricing
    summary,
    finalSummary,

}), [
    paymentMethod,
    gateways,
    selectedGateway,
    selectGateway,
    gatewayLoading,

    wallet,
    walletLoading,
    useWallet,
    toggleWallet,

    couponData,
    couponCode,
    couponLoading,
    couponStatus,
    couponMessage,

    applyCoupon,
    removeCoupon,

    summary,
    finalSummary,
]);


    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
}


export function usePayment() {
    const context =
        useContext(PaymentContext);

    if (!context) {
        throw new Error(
            "usePayment must be used inside PaymentProvider"
        );
    }

    return context;
}